import React from 'react';
import { motion } from 'framer-motion';

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white text-primary animate-fade-in">
      <div className="max-w-5xl mx-auto px-6 py-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-widest uppercase text-nude mb-3">
            Our Story
          </p>
          <img
            src="/logo.png"
            alt="Lanre Savage"
            className="h-20 w-auto mx-auto"
          />
        </motion.div>

        {/* Founder Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="aspect-[4/5] bg-light overflow-hidden"
          >
            <img
              src="/founder-placeholder.jpg"
              alt="Lanre Savage, Founder"
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <p className="text-xs tracking-widest uppercase text-secondary mb-3">
              Meet the Founder
            </p>
            <h1
              className="text-5xl mb-2 leading-tight text-primary tracking-wide"
              style={{ fontFamily: "'Scaver', serif", fontWeight: 400 }}
            >
              Lanre Savage
            </h1>
            <p className="text-xs tracking-widest uppercase text-secondary mb-8">
              Lagos, Nigeria
            </p>

            <div className="space-y-4 text-sm text-secondary leading-relaxed tracking-wide">
              <p>
                Fashion has always been more than clothing to me — it is a
                form of self-expression, confidence, culture, and identity.
              </p>
              <p>
                My name is Lanre Savage, born in Lagos, Nigeria. 
                The vision for Lanre Savage Collection was first
                conceived in 2010, shortly after graduating from university.
                What began as a passion for style and design gradually
                evolved into a deeper ambition: to create a fashion brand
                that celebrates individuality while honoring the richness of
                African culture through a modern lens.
              </p>
              <p>
                As someone who genuinely loves fashion, I have always been
                fascinated by the art of mixing and matching different
                elements, textures, colors, and influences to create
                something distinctive and meaningful. This creative
                curiosity has become the foundation of Lanre Savage
                Collection.
              </p>
              <p>
                Our designs are an infusion of modern African attire and
                contemporary fashion, blending heritage, elegance, and
                innovation. We believe fashion should be versatile,
                sophisticated, and timeless — allowing people to embrace
                their culture while expressing their unique personal style.
              </p>
              <p>
                At Lanre Savage Collection, every piece is designed with
                intention. We aim to create garments that inspire
                confidence, celebrate authenticity, and make a lasting
                impression. Whether worn for a special occasion or everyday
                elegance, our collections are crafted for individuals who
                appreciate quality, creativity, and self-expression.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center py-16 border-t border-light"
        >
          <p className="font-serif text-3xl md:text-4xl italic mb-4 max-w-2xl mx-auto leading-snug text-primary">
            "When you look good, you feel good."
          </p>
          <p className="text-xs tracking-widest uppercase text-secondary">
            — Lanre Savage
          </p>
        </motion.div>

        {/* The Future */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="border-t border-light pt-16"
        >
          <p className="text-xs tracking-widest uppercase text-secondary mb-3 text-center">
            The Future
          </p>
          <p className="text-sm text-secondary leading-relaxed tracking-wide max-w-2xl mx-auto text-center mb-12">
            Looking ahead, my vision is to grow Lanre Savage Collection into
            a globally recognized fashion house known for redefining modern
            African fashion. I envision a brand that bridges cultures,
            inspires future generations, and showcases African creativity
            on the world stage while remaining true to its roots.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { title: 'Heritage', desc: 'Honoring our roots and the beauty of African culture.' },
              { title: 'Craftsmanship', desc: 'Precision, quality and attention to every detail.' },
              { title: 'Innovation', desc: 'Blending tradition with modern creativity.' },
              { title: 'Impact', desc: 'Inspiring confidence and leaving a lasting impression.' },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-10 h-px bg-secondary mx-auto mb-4" />
                <h3 className="text-xs tracking-widest uppercase mb-3 text-primary">
                  {item.title}
                </h3>
                <p className="text-xs text-secondary leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Newsletter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="border-t border-light mt-16 pt-16 text-center"
        >
          <p className="text-xs tracking-widest uppercase text-secondary mb-2">
            Join the Journey
          </p>
          <h2 className="font-serif text-3xl mb-6 text-primary">
            Stay Close to the Collection
          </h2>
          <NewsletterForm />
        </motion.div>
      </div>
    </div>
  );
};

const NewsletterForm: React.FC = () => {
  const [email, setEmail] = React.useState('');
  const [website, setWebsite] = React.useState(''); // honeypot — real users leave this blank
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      setLoading(true);
      const api = (await import('../api/axios')).default;
      const { getRecaptchaToken } = await import('../utils/recaptcha');
      const recaptcha_token = await getRecaptchaToken('newsletter_subscribe');
      await api.post('/api/newsletter/subscribe', { email, website, recaptcha_token });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <p className="text-xs tracking-widest uppercase text-secondary">
        Thank you. We'll keep you close to the collection.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        aria-hidden="true"
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="YOUR EMAIL"
        required
        className="flex-1 bg-transparent border-b border-secondary text-primary text-xs tracking-widest py-3 outline-none placeholder-secondary focus:border-primary transition-colors w-full"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-primary text-white px-8 py-3 text-xs tracking-widest uppercase hover:bg-secondary transition-colors whitespace-nowrap disabled:opacity-50"
      >
        {loading ? 'Submitting...' : 'Subscribe'}
      </button>
    </form>
  );
};

export default AboutPage;